
import React from 'react';
import PageLayout from '@/components/layout/PageLayout';
import PageHero from '@/components/ui/PageHero';
import ScrollReveal from '@/components/ui/ScrollReveal';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Award, Heart, Map, Calendar, Quote, ArrowRight } from 'lucide-react';

const About = () => {
  const favoriteFood = [
    "Mexican Tacos & Chilaquiles",
    "Fried Chicken Sandwich or Nashville Hot Chicken",
    "Sushi & Asian Food Overall",
    "Authentic Italian",
    "Xi'an Famous Foods from NYC"
  ];

  const travelDestinations = [
    "New York City",
    "Paris",
    "Rome",
    "Milan"
  ];

  const weeklyActivities = [
    "Morning Stretching",
    "Deep Breathing",
    "Reading the Bible and Praying",
    "Time with Those Closest",
    "Rarely, Playing Battlefield",
    "Working on Experimental Projects",
    "Exploring New Tech"
  ];

  const values = [
    {
      title: "Creativity",
      description: "Finding innovative solutions to complex problems through creative thinking and unique approaches."
    },
    {
      title: "Quality",
      description: "Delivering exceptional work that exceeds expectations and stands the test of time."
    },
    {
      title: "Authenticity",
      description: "Being genuine in all interactions and creating work that truly represents clients' unique voices."
    },
    {
      title: "Collaboration",
      description: "Working closely with clients as partners to achieve shared goals and mutual success."
    }
  ];

  return (
    <PageLayout 
      title="About Evan | Astrem"
      description="Learn more about Evan, the founder of Astrem, and his approach to web design, photography, marketing, and technology solutions."
    >
      <PageHero 
        title="About Evan" 
        subtitle="The Person Behind Astrem"
        description="I'm a passionate creative professional specializing in web design, photography, marketing, and innovative technology solutions. Learn more about my background, values, and approach to helping clients succeed."
        backgroundImage="https://portal.astrem.co/users/u9/9466/astremagency/lovableagentsite/Man_working_on_laptop_outside.jpg"
        ctaText="Get in Touch"
        ctaLink="/contact"
      />

      {/* Bio Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <ScrollReveal>
              <div>
                <span className="inline-block px-3 py-1 bg-astrem-gray-100 text-astrem-gray-800 rounded-full text-sm font-medium mb-4">
                  My Story
                </span>
                <h2 className="text-3xl md:text-4xl font-display font-medium mb-6">
                  Hello, I'm Evan
                </h2>
                <div className="space-y-4 text-lg text-astrem-gray-600">
                  <p>
                    I'm the founder of Astrem, a creative studio specializing in web design, photography, marketing, and innovative technology solutions. With a background spanning over a decade in digital media and technology, I've developed a unique approach to helping businesses and individuals bring their visions to life.
                  </p>
                  <p>
                    My journey began with a passion for both technology and visual storytelling, which led me to explore various creative fields before founding Astrem. I believe in the power of thoughtful design, authentic storytelling, and strategic technology implementation to help clients achieve their goals.
                  </p>
                  <p>
                    Based in Baton Rouge, Louisiana, I work with clients locally and across the country, bringing a personalized approach to each project. I'm dedicated to understanding your unique needs and delivering solutions that not only meet your objectives but exceed your expectations.
                  </p>
                </div>
              </div>
            </ScrollReveal>
            
            <ScrollReveal delay={200}>
              <div className="relative">
                <img
                  src="https://portal.astrem.co/users/u9/9466/astremagency/lovableagentsite/Man_working_on_laptop_outside.jpg"
                  alt="Evan portrait"
                  className="rounded-lg shadow-lg w-full h-auto object-cover"
                />
                <div className="absolute -bottom-6 -right-6 bg-white p-6 rounded-lg shadow-md hidden md:block">
                  <Quote className="h-12 w-12 text-primary/20 absolute -top-2 -left-2" />
                  <p className="text-lg italic relative z-10">
                    It's about the little things too.
                  </p>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-astrem-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center max-w-3xl mx-auto mb-16">
              <span className="inline-block px-3 py-1 bg-astrem-gray-100 text-astrem-gray-800 rounded-full text-sm font-medium mb-4">
                My Values
              </span>
              <h2 className="text-3xl md:text-4xl font-display font-medium mb-6">What Drives My Work</h2>
              <p className="text-lg text-astrem-gray-600">
                These core principles guide everything I do and shape how I approach each project.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {values.map((value, index) => (
              <ScrollReveal key={index} delay={index * 100}>
                <div className="bg-white p-8 rounded-lg shadow-sm border border-astrem-gray-200">
                  <Award className="h-10 w-10 text-primary mb-4" />
                  <h3 className="text-xl font-medium mb-3">{value.title}</h3>
                  <p className="text-astrem-gray-600">{value.description}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Fun Facts Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center max-w-3xl mx-auto mb-16">
              <span className="inline-block px-3 py-1 bg-astrem-gray-100 text-astrem-gray-800 rounded-full text-sm font-medium mb-4">
                Get to Know Me
              </span>
              <h2 className="text-3xl md:text-4xl font-display font-medium mb-6">Beyond the Work</h2>
              <p className="text-lg text-astrem-gray-600">
                A few fun facts about me outside of my professional life.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            <ScrollReveal delay={100}>
              <div className="text-center">
                <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Heart className="h-10 w-10 text-primary" />
                </div>
                <h3 className="text-2xl font-medium mb-6">Favorite Foods</h3>
                <ul className="space-y-4">
                  {favoriteFood.map((food, index) => (
                    <li key={index} className="flex items-center justify-center">
                      <span className="inline-block w-3 h-3 bg-primary rounded-full mr-3"></span>
                      <span className="text-lg text-astrem-gray-700">{food}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={200}>
              <div className="text-center">
                <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Map className="h-10 w-10 text-primary" />
                </div>
                <h3 className="text-2xl font-medium mb-6">Places I've Traveled</h3>
                <ul className="space-y-4">
                  {travelDestinations.map((place, index) => (
                    <li key={index} className="flex items-center justify-center">
                      <span className="inline-block w-3 h-3 bg-primary rounded-full mr-3"></span>
                      <span className="text-lg text-astrem-gray-700">{place}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={300}>
              <div className="text-center">
                <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Calendar className="h-10 w-10 text-primary" />
                </div>
                <h3 className="text-2xl font-medium mb-6">Weekly Activities</h3>
                <ul className="space-y-4">
                  {weeklyActivities.map((activity, index) => (
                    <li key={index} className="flex items-center justify-center">
                      <span className="inline-block w-3 h-3 bg-primary rounded-full mr-3"></span>
                      <span className="text-lg text-astrem-gray-700">{activity}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Skills Section */}
      <section className="py-20 bg-astrem-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center max-w-3xl mx-auto mb-16">
              <span className="inline-block px-3 py-1 bg-astrem-gray-100 text-astrem-gray-800 rounded-full text-sm font-medium mb-4">
                My Expertise
              </span>
              <h2 className="text-3xl md:text-4xl font-display font-medium mb-6">Areas of Specialization</h2>
              <p className="text-lg text-astrem-gray-600">
                I've developed expertise across multiple disciplines to provide comprehensive solutions.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <ScrollReveal delay={100}>
              <div className="bg-white p-6 rounded-lg shadow-sm border border-astrem-gray-200 h-full">
                <h3 className="text-xl font-medium mb-3">Web Design & Development</h3>
                <p className="text-astrem-gray-600 mb-4">
                  Creating beautiful, functional websites optimized for performance and user experience.
                </p>
                <div className="space-y-2">
                  <div className="w-full bg-astrem-gray-100 rounded-full h-2">
                    <div className="bg-primary h-2 rounded-full w-[95%]"></div>
                  </div>
                  <div className="flex justify-between text-sm text-astrem-gray-500">
                    <span>HTML/CSS</span>
                    <span>95%</span>
                  </div>
                </div>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={150}>
              <div className="bg-white p-6 rounded-lg shadow-sm border border-astrem-gray-200 h-full">
                <h3 className="text-xl font-medium mb-3">Photography</h3>
                <p className="text-astrem-gray-600 mb-4">
                  Capturing compelling images that tell stories and showcase subjects at their best.
                </p>
                <div className="space-y-2">
                  <div className="w-full bg-astrem-gray-100 rounded-full h-2">
                    <div className="bg-primary h-2 rounded-full w-[90%]"></div>
                  </div>
                  <div className="flex justify-between text-sm text-astrem-gray-500">
                    <span>Composition & Lighting</span>
                    <span>90%</span>
                  </div>
                </div>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={200}>
              <div className="bg-white p-6 rounded-lg shadow-sm border border-astrem-gray-200 h-full">
                <h3 className="text-xl font-medium mb-3">Digital Marketing</h3>
                <p className="text-astrem-gray-600 mb-4">
                  Implementing strategic marketing solutions to increase visibility and engagement.
                </p>
                <div className="space-y-2">
                  <div className="w-full bg-astrem-gray-100 rounded-full h-2">
                    <div className="bg-primary h-2 rounded-full w-[85%]"></div>
                  </div>
                  <div className="flex justify-between text-sm text-astrem-gray-500">
                    <span>Strategy & Implementation</span>
                    <span>85%</span>
                  </div>
                </div>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={250}>
              <div className="bg-white p-6 rounded-lg shadow-sm border border-astrem-gray-200 h-full">
                <h3 className="text-xl font-medium mb-3">Technology Innovation</h3>
                <p className="text-astrem-gray-600 mb-4">
                  Exploring cutting-edge technologies to create unique solutions and experiences.
                </p>
                <div className="space-y-2">
                  <div className="w-full bg-astrem-gray-100 rounded-full h-2">
                    <div className="bg-primary h-2 rounded-full w-[88%]"></div>
                  </div>
                  <div className="flex justify-between text-sm text-astrem-gray-500">
                    <span>Technical Implementation</span>
                    <span>88%</span>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-astrem-black text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <ScrollReveal>
              <h2 className="text-3xl md:text-4xl font-display font-medium mb-6">
                Let's Work Together
              </h2>
            </ScrollReveal>
            
            <ScrollReveal delay={100}>
              <p className="text-lg text-astrem-gray-300 mb-8">
                I'm always excited to take on new challenges and help clients bring their visions to life. Whether you have a specific project in mind or just want to explore possibilities, I'd love to connect.
              </p>
            </ScrollReveal>
            
            <ScrollReveal delay={200}>
              <Button asChild size="lg" className="bg-white text-astrem-black hover:bg-astrem-gray-100 rounded-full px-8">
                <Link to="/contact">
                  Get in Touch
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </ScrollReveal>
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

export default About;
